<template>
  <div>
    <Bar
      :chartData="ChartData"
      :chart-options="chartOptions"
      :plugins="chartPlugins"
      :height="200"
    />
  </div>
</template>

<script>
import {
  Chart,
  registerables,
} from 'chart.js';
import { Bar } from 'vue-chartjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CHART_COLORS, rgbString } from '@/constants/color';

Chart.register(...registerables);

export default {
  name: 'BarChart',
  components: {
    Bar,
  },
  props: {
    Axis: {
      type: String,
      default: 'x',
    },
    labelsData: {
      type: Array,
      default: null,
    },
    datasetsData: {
      type: Object,
      default: null,
    },
    sort: { // 入力により昇順、降順でソートする
      type: String,
      default: null,
    },
    options: { // 個別にグラフスタイルをいじりたい時は呼び出し元でオプションを設定
      type: Object,
      default: null,
    },
    showDataLabels: { // データラベル（棒グラフ上の数値）の表示・非表示
      type: Boolean,
      default: false,
    },
    colorMode: { // モード別の色味切り替え（text, online, onlineOnly, virtualOnly）
      type: String,
      default: null,
    },
  },
  data() {
    return {
      ChartData: {
        labels: [],
        datasets: [],
      },
      chartOptions: {
        indexAxis: null,
        layout: {
          padding: {
            top: 5,
            right: 10,
            bottom: 5,
            left: 10,
          },
        },
        plugins: {
          legend: {
            display: false, // 凡例の非表示
          },
          datalabels: {
            display: false, // デフォルトは非表示（propsで制御）
          },
        },
        scales: {
          x: {
            ticks: {
              stepSize: 1, // メモリのステップサイズを1.0に設定（標準は自動なので少ないと0.25になったりする）
            },
          },
        },
        // barThickness: 20, // バーの太さをpxで指定
        maxBarThickness: 40, // バーの最大の太さを40pxに制限
        barPercentage: 0.7, // バーの太さを余白と相対的に指定
        responsive: true,
      },
      chartPlugins: [], // プラグインの配列
    };
  },
  created() {
    this.chartOptions.indexAxis = this.Axis;

    // showDataLabelsがtrueの場合、データラベルを表示
    if (this.showDataLabels) {
      this.chartPlugins = [ChartDataLabels]; // プラグインを個別に登録

      // データラベル表示時は右側のパディングを増やす（外側ラベル用のスペース確保）
      this.chartOptions.layout.padding.right = 50;

      const { threshold } = CHART_COLORS.horizontalBar.dataLabels;

      this.chartOptions.plugins.datalabels = {
        display: true,
        font: {
          weight: 'bold',
          size: 18,
        },
        textStrokeColor: '#fff',
        textStrokeWidth: 1.2,
        // 値が小さい場合は棒の外側、大きい場合は内側に表示
        anchor: () => 'end', // 常に終端
        align: (context) => {
          const value = context.dataset.data[context.dataIndex];
          return value < threshold ? 'end' : 'start'; // 閾値未満は外側、以上は内側
        },
        offset: (context) => {
          const value = context.dataset.data[context.dataIndex];
          return value < threshold ? 2 : -2; // 閾値未満は外側に2px、以上は内側に2px
        },
      };
    }
  },
  mounted() {
    this.drawGraph();
  },
  watch: {
    labelsData() { // 期日や絞り込み条件の変動があったら再描画
      this.updateGraph();
    },
  },
  methods: {
    // colorModeに対応する開始色・終了色を返す（類似色グラデーション）
    getGradientColorsForMode(mode) {
      return CHART_COLORS.horizontalBar.modeGradient[mode] || null;
    },
    // 開始色と終了色のRGB線形補間で各バーの色を生成する
    generateGradientColors(startColor, endColor, count) {
      return Array.from({ length: count }, (_, i) => {
        const t = count === 1 ? 0 : i / (count - 1);
        const r = Math.round(startColor.r + (endColor.r - startColor.r) * t);
        const g = Math.round(startColor.g + (endColor.g - startColor.g) * t);
        const b = Math.round(startColor.b + (endColor.b - startColor.b) * t);
        return rgbString({ r, g, b });
      });
    },
    // datasetsのbackgroundColorをcolorModeに応じたグラデーションに差し替える
    applyModeColors() {
      if (!this.colorMode) return;
      const gradient = this.getGradientColorsForMode(this.colorMode);
      if (!gradient) return;

      this.ChartData.datasets.forEach((dataset) => {
        const count = dataset.data.length;
        dataset.backgroundColor = this.generateGradientColors(gradient.start, gradient.end, count);
      });
    },
    updateGraph() {
      this.ChartData.labels = [];
      this.ChartData.datasets = [];
      this.drawGraph();
    },
    drawGraph() {
      this.labelsData.forEach((elem) => {
        this.ChartData.labels.push(elem);
      });

      // 複数軸の場合はChartData.datasetsを配列にする
      if (this.datasetsData.length) {
        this.datasetsData.forEach((elem) => {
          this.ChartData.datasets.push(elem);
        });
      } else {
        this.ChartData.datasets.push(this.datasetsData);
      }

      if (this.options) {
        Object.keys(this.options).forEach((elem) => {
          this.chartOptions[elem] = this.options[elem];
        });
      }

      if (this.sort) { // ソート
        const sortContainer = [];
        this.ChartData.labels.forEach((elem, index) => {
          const data = {
            label: elem,
            data: this.ChartData.datasets[0].data[index],
            color: this.ChartData.datasets[0].backgroundColor[index],
          };
          sortContainer.push(data);
        });
        const sortedData = sortContainer.sort((a, b) => a.data - b.data);
        if (this.sort === 'desc') { // 降順
          sortedData.reverse();
        }
        this.ChartData.labels = [];
        this.ChartData.datasets = [{
          data: [],
          backgroundColor: [],
          label: '相談内容',
        }];

        sortedData.forEach((elem) => {
          this.ChartData.labels.push(elem.label);
          this.ChartData.datasets[0].data.push(elem.data);
          this.ChartData.datasets[0].backgroundColor.push(elem.color);
        });
      }

      // colorModeが指定されている場合、濃淡カラーを適用（ソート後に実行）
      this.applyModeColors();
    },
  },
};
</script>

